import { ConfigProvider } from 'antd';
import { useEffect, useMemo, useReducer } from 'react';
import { createGlobalStyle, css } from 'styled-components';
import { CompanyTheme } from '../../../api/types';
import { CUPRA_THEME_CLASSNAME } from '../../../themes/cupra/CupraGlobalStyles';
import { DEFAULT_THEME_CLASSNAME } from '../../../themes/default/DefaultGlobalStyles';
import { PORSCHE_THEME_CLASSNAME } from '../../../themes/porsche/PorscheGlobalStyles';
import { PORSCHE_V3_THEME_CLASSNAME } from '../../../themes/porscheV3/PorscheV3GlobalStyles';
import { SKODA_THEME_CLASSNAME } from '../../../themes/skoda/SkodaGlobalStyles';
import { VW_THEME_CLASSNAME } from '../../../themes/volkswagen/VWGlobalStyles';
import { ThemeControllerContext, type ThemeOverride, type Action } from './Context';

export type ThemeControllerProps = { children: JSX.Element };

const getFontType = (fontUrl: string) => {
    const extension = fontUrl.toLowerCase().split('.').slice(-1)[0];

    switch (extension) {
        case 'woff':
        case 'woff2':
            return extension;

        case 'ttf':
            return 'truetype';

        case 'otf':
            return 'opentype';

        default:
            throw new Error(`Unsupported format ${extension}`);
    }
};

const getThemeClassName = (theme: CompanyTheme) => {
    switch (theme) {
        case CompanyTheme.Default:
            return DEFAULT_THEME_CLASSNAME;

        case CompanyTheme.Porsche:
            return PORSCHE_THEME_CLASSNAME;

        case CompanyTheme.Volkswagen:
            return VW_THEME_CLASSNAME;

        case CompanyTheme.Skoda:
            return SKODA_THEME_CLASSNAME;

        case CompanyTheme.PorscheV3:
            return PORSCHE_V3_THEME_CLASSNAME;

        case CompanyTheme.Cupra:
            return CUPRA_THEME_CLASSNAME;

        default:
            return `theme-${theme.toLowerCase()}`;
    }
};

const GlobalStyle = createGlobalStyle<{ currentTheme: ThemeOverride }>`
    :root {
        --mobility-page-max-width: 120rem;
    }

    ${props =>
        !props.currentTheme.font &&
        !props.currentTheme.fontBold &&
        css`
            font-family: 'Roboto', sans-serif !important;
        `}

    ${props =>
        props.currentTheme.theme === CompanyTheme.PorscheV3 &&
        css`
            html,
            body {
                font-family: var(--font-family) !important;
            }
        `}

    ${props =>
        (props.currentTheme.font || props.currentTheme.fontBold) &&
        props.currentTheme.theme !== CompanyTheme.PorscheV3 &&
        css`
            ${props.currentTheme.font &&
            `
                @font-face {
                    font-family: 'CustomerFont-${props.currentTheme.id}';
                    src: url('${props.currentTheme.font}') format('${getFontType(props.currentTheme.font)}');
                    font-weight: normal;

                    // this make first text render a little faster
                    // https://web.dev/font-best-practices/
                    font-display: swap;
                }
            `}

            ${props.currentTheme.fontBold &&
            `
                @font-face {
                    font-family: 'CustomerFont-${props.currentTheme.id}';
                    src: url('${props.currentTheme.fontBold}') format('${getFontType(props.currentTheme.fontBold)}');
                    font-weight: bold;

                    // this make first text render a little faster
                    // https://web.dev/font-best-practices/
                    font-display: swap;
                }
            `}

            html,
            body {
                font-family: 'CustomerFont-${props.currentTheme.id}', 'Roboto', sans-serif !important;
            }
        `}
`;

type State = ThemeOverride[];

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'register':
            return [...state, action.override];

        case 'unregister':
            return state.filter(override => override.id === action.id);

        case 'update': {
            const index = state.findIndex(override => override.id === action.override.id);

            if (index >= 0) {
                // clone the state
                const newState = [...state];
                // replace the element
                newState[index] = action.override;

                return newState;
            }

            return state;
        }

        default:
            return state;
    }
};

export const ThemeController = ({ children }: ThemeControllerProps) => {
    const [overrides, dispatch] = useReducer(reducer, []);

    const currentTheme = useMemo(
        () =>
            overrides.reduce<ThemeOverride>(
                (acc, override) => ({
                    ...acc,
                    ...override,
                }),
                {
                    font: null,
                    fontBold: null,
                    id: 'none',
                    primaryColor: '#1890ff',
                    theme: CompanyTheme.Default,
                }
            ),
        [overrides]
    );

    useEffect(() => {
        ConfigProvider.config({
            theme: {
                primaryColor: currentTheme.primaryColor,
            },
        });

        // remove theme specific class names used in global styles
        Object.values(CompanyTheme)
            .filter(value => currentTheme.theme !== value)
            .map(getThemeClassName)
            .map(value => document.documentElement.classList.remove(value));

        document.documentElement.classList.add(getThemeClassName(currentTheme.theme));

        return () => {
            document.documentElement.classList.remove(getThemeClassName(currentTheme.theme));
        };
    }, [currentTheme]);

    return (
        <ThemeControllerContext.Provider value={dispatch}>
            <GlobalStyle currentTheme={currentTheme} />
            {children}
        </ThemeControllerContext.Provider>
    );
};
